import { I18n } from '@calendly/i18n';
import { LocationPinIcon, PhoneIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { WidgetContainer } from '../widget_container';
import { VideoConferencingDetails } from './video_conferencing_details';
const PHONE_LOCATION_KINDS = ['inbound_call', 'outbound_call'];
const toLower = v => v && String(v).toLowerCase() || '';
const VIDEO_KIND_LABELS = {
  zoom: 'Zoom',
  zoom_conference: 'Zoom',
  google_meet: 'Google Meet',
  google_conference: 'Google Meet',
  microsoft_teams: 'Microsoft Teams',
  microsoft_teams_conference: 'Microsoft Teams',
  webex: 'Webex',
  webex_conference: 'Webex',
  gotomeeting: 'GoToMeeting'
};
// Only kinds that have a video join UI (excludes physical, custom, ask_invitee, and phone — phone uses LocationDetails)
const VIDEO_CONFERENCING_KINDS = Object.keys(VIDEO_KIND_LABELS);
const KINDS_THAT_USE_JOIN_UI = [...VIDEO_CONFERENCING_KINDS];
const locationIconCss = "lob8b23";
const LocationIcon = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(locationIconCss, props.className)} />;
});
const textContainerCss = "t19afqqe";
const TextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textContainerCss, props.className)} />;
});
const addressCss = "a1rpy73w";
const Address = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(addressCss, props.className)} />;
});
const NoLocationDetails = () => <TextContainer>
    <BodyText weight="bold" color={colorTextStandard}>{I18n.t('publisher.meetings.location.no_location')}</BodyText>
  </TextContainer>;
const LocationDetails = ({
  location,
  locationType,
  locationAdditionalInfo
}) => {
  const locationTypeNorm = toLower(locationType);
  return <Address>
      <LocationIcon>
        {PHONE_LOCATION_KINDS.includes(locationTypeNorm) ? <PhoneIcon size="x-large" /> : <LocationPinIcon size="x-large" />}
      </LocationIcon>
      {location ? <TextContainer>
          <BodyText weight="semibold" color={colorTextStandard}>{location}</BodyText>
          {locationAdditionalInfo && <BodyText color="muted">{locationAdditionalInfo}</BodyText>}
        </TextContainer> : <NoLocationDetails />}
    </Address>;
};
export const LocationsWidget = ({
  event
}) => {
  const {
    location,
    locationType,
    locationKind,
    locationAdditionalInfo,
    externalLocation,
    pageMembers,
    cancelled,
    past
  } = event;
  const locationKindNorm = toLower(locationKind);
  const locationTypeNorm = toLower(locationType);
  const kindForCheck = locationTypeNorm || locationKindNorm;
  const hasExternalLocation = externalLocation && (externalLocation.joinUrl || externalLocation.kind);
  const externalKindNorm = hasExternalLocation ? toLower(externalLocation.kind) : '';
  const useVideoFromExternal = hasExternalLocation && KINDS_THAT_USE_JOIN_UI.includes(externalKindNorm);
  const isPhoneKind = kindForCheck && PHONE_LOCATION_KINDS.includes(kindForCheck);
  const kindForIcon = k => k === 'zoom_conference' ? 'zoom' : k;
  const syntheticExternalLocation = location && kindForCheck && !useVideoFromExternal && VIDEO_CONFERENCING_KINDS.includes(kindForCheck) ? {
    kind: kindForIcon(kindForCheck),
    name: VIDEO_KIND_LABELS[kindForCheck] || kindForCheck,
    joinUrl: location,
    oneTapMobile: [],
    phoneNumber: '',
    formattedMeetingId: '',
    intlNumbersUrl: ''
  } : null;

  // Use friendly display names for both API externalLocation and synthetic locations (e.g. "Zoom" not "ZOOM")
  const locationToShow = useVideoFromExternal ? {
    ...externalLocation,
    name: VIDEO_KIND_LABELS[externalKindNorm] || externalLocation.name
  } : syntheticExternalLocation;
  const effectiveKind = locationToShow ? toLower(locationToShow.kind) : kindForCheck;
  const isPhone = isPhoneKind || effectiveKind && PHONE_LOCATION_KINDS.includes(effectiveKind);

  // Phone calls: show only the number with phone icon, not the video-conferencing UI
  const phoneDisplayText = isPhone && (useVideoFromExternal && externalLocation?.phoneNumber || location);
  const phoneDisplay = phoneDisplayText ? String(phoneDisplayText).replace(/^tel:/i, '').trim() || phoneDisplayText : null;
  const shouldHide = externalLocation?.name === 'Custom' && toLower(externalLocation?.kind) === 'custom' && !externalLocation?.joinUrl?.trim();
  if (shouldHide) return null;
  const showVideoJoin = locationToShow && KINDS_THAT_USE_JOIN_UI.includes(toLower(locationToShow.kind));
  let content;
  if (isPhone) {
    content = <LocationDetails location={phoneDisplay} locationType={locationType || kindForCheck || locationToShow?.kind} locationAdditionalInfo={locationAdditionalInfo} />;
  } else if (showVideoJoin) {
    content = <VideoConferencingDetails location={locationToShow} hosts={pageMembers || []} cancelled={cancelled} past={past} />;
  } else {
    content = <LocationDetails location={location} locationType={locationType} locationAdditionalInfo={locationAdditionalInfo} />;
  }
  return <WidgetContainer title={I18n.t('publisher.meetings.location.location')} showBorder={false} noPadding sectionWithBottomBorder>
      {content}
    </WidgetContainer>;
};

require("./locations_widget.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./locations_widget.js");